import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import Button from 'src/components/primitives/Button/Button';
import { PageRoute } from 'src/constants/constants';
import { ArrowRight } from '@drift-labs/icons';
import usePortfolioReferralData from 'src/components/PortfolioPage/PortfolioEarnSubPage/hooks/usePortfolioReferralData';
import { BigNum } from '@drift-labs/sdk';
import { cj, cm } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';
import NoReferralLinkCard from 'src/components/ReferralsPageContent/NoReferralLinkCard';

export default function PortfolioEarnReferralCard() {
	const {
		data: {
			referrerNameAccounts,
			referralRewards,
			referralCount,
			referredVolume30D,
		},
		isLoading,
	} = usePortfolioReferralData();

	const content = (() => {
		if (referrerNameAccounts.accounts.length > 0) {
			return (
				<ReferralStatsCard
					referralRewards={referralRewards}
					referredVolume30D={referredVolume30D}
					referralCount={referralCount}
					isLoading={isLoading}
				/>
			);
		}

		return <NoReferralLinkCard />;
	})();

	return (
		<div className="flex flex-col gap-y-lg pb-2xl">
			<h3 className="typo-t2 text-text-default">Referral Rewards</h3>
			{content}
		</div>
	);
}

interface ReferralStatsCardProps {
	referralRewards: BigNum | undefined;
	referredVolume30D: BigNum | undefined;
	referralCount: BigNum | undefined;
	isLoading: boolean;
}

function ReferralStatsCard({
	referralRewards,
	referredVolume30D,
	referralCount,
	isLoading,
}: ReferralStatsCardProps) {
	return (
		<div
			className={cj(
				'flex flex-col justify-between gap-y-lg sm:flex-row sm:items-center',
				'rounded-sm bg-container-bg p-md sm:p-2xl'
			)}
		>
			<div className="grid basis-1/2 grid-cols-2 gap-x-12 gap-y-lg sm:flex">
				<ReferralStatsCardItem
					className="not-sm:items-start"
					label="Total Rewards Earned"
					value={referralRewards}
					valueClassName="text-text-positive-green-button"
					displayType="currency"
					isLoading={isLoading}
					isAccountValueToHide
				/>
				<ReferralStatsCardItem
					className="not-sm:items-end"
					label="30D Referral Trade Volume"
					value={referredVolume30D}
					displayType="currency"
					isLoading={isLoading}
					isAccountValueToHide
				/>
				<ReferralStatsCardItem
					className="not-sm:items-start"
					label="Total Referrals"
					value={referralCount}
					isLoading={isLoading}
					displayType="number"
				/>
			</div>

			<Button
				size="md"
				as="a"
				href={`/${PageRoute.referrals}`}
				rightIcon={ArrowRight}
			>
				Referral Details
			</Button>
		</div>
	);
}

interface ReferralStatsCardItemProps extends WithClassName {
	label: string;
	value: BigNum | undefined;
	valueClassName?: string;
	displayType: 'currency' | 'number';
	isAccountValueToHide?: boolean;
	isLoading: boolean;
}

function ReferralStatsCardItem({
	label,
	value,
	valueClassName,
	className,
	displayType,
	isAccountValueToHide,
	isLoading,
}: ReferralStatsCardItemProps) {
	const isCurrencyDisplay = displayType === 'currency';

	return (
		<div className={cj('flex flex-col gap-y-xs whitespace-nowrap', className)}>
			<h3 className="typo-b4 text-text-secondary">{label}</h3>
			<NumberDisplay
				value={value}
				type={displayType}
				decimals={isCurrencyDisplay ? 2 : undefined}
				millify={{ threshold: 100_000, rounded: true }}
				className={cm('typo-t1 text-text-default', valueClassName)}
				loading={isLoading}
				skeletonClassName="w-20 h-4 sm:h-6"
				isAccountValueToHide={isAccountValueToHide}
			/>
		</div>
	);
}
